import { resolveTopologicalPathForBlock } from '@festina-life/api'
import { useTranslate } from '@festina-life/ui/hooks'
import { FilterCheckbox } from '@festina-life/ui/listsAndTables'
import { useMemo } from 'react'

import { useMetaModelContext } from '../../context/metaModelContext'
import { collectAllNodesFromTree } from '../../uiModels/api/metaModel/helpers'
import { ManageHeader } from '../manageHeader/ManageHeader'
import { BlockTreeNodes } from './BlockTreeNodes'
import type { BlockFilter } from './types'

type Props = {
  blockFilter: BlockFilter
  onBlockFilterChanged: (blockFilter: BlockFilter) => void
}

export const BlockFilterSection = ({
  blockFilter,
  onBlockFilterChanged,
}: Props) => {
  const translate = useTranslate()
  const { blockTopology } = useMetaModelContext()
  const allBlocks = useMemo(
    () => collectAllNodesFromTree(blockTopology),
    [blockTopology],
  )
  const hasMultipleBlocks = allBlocks.length > 1
  const isAllBlocks = blockFilter.mode === 'all'

  const baseBlocks = useMemo(() => {
    if (blockFilter.mode !== 'withBaseBlocks' || !blockFilter.selectedBlock) {
      return new Set<string>()
    }

    const path = resolveTopologicalPathForBlock(
      blockFilter.selectedBlock,
      blockTopology,
    )
    return new Set(path.filter((block) => block !== blockFilter.selectedBlock))
  }, [blockFilter, blockTopology])

  return hasMultipleBlocks ? (
    <>
      <ManageHeader
        title={translate('configurationItemsTree.filter.block')}
        isFaded
      />
      <div className="flex flex-col gap-y-2">
        <FilterCheckbox
          label={translate('configurationItemsTree.filter.allBlocks')}
          checked={isAllBlocks}
          onChange={() =>
            onBlockFilterChanged({ mode: 'all', selectedBlock: null })
          }
        />
        <BlockTreeNodes
          tree={blockTopology}
          blockFilter={blockFilter}
          baseBlocks={baseBlocks}
          onBlockFilterChanged={onBlockFilterChanged}
        />
        {!isAllBlocks && (
          <FilterCheckbox
            label={translate('configurationItemsTree.filter.includeBaseBlocks')}
            checked={blockFilter.mode === 'withBaseBlocks'}
            onChange={(includeBase) =>
              onBlockFilterChanged({
                ...blockFilter,
                mode: includeBase ? 'withBaseBlocks' : 'single',
              })
            }
          />
        )}
      </div>
    </>
  ) : null
}
